import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, ChevronDown, AlertTriangle, Paperclip,
  FileText, Send, Forward, Activity, CheckSquare, Edit3, Users, Trash2,
  Check, Pause, RotateCcw, Plus, MessageSquare, Loader, Download,
  Play, Clock, Calendar, AlignLeft, Upload } from "lucide-react";
import axiosInstance from "../../API/interceptor";
import RichTextEditor from "@dietary-business-intelligence/textora";
import { Editor } from "primereact/editor";
import "primereact/resources/themes/lara-light-purple/theme.css";
import "primereact/resources/primereact.min.css";


function EditorWrapper({ editorNode }) {
  const divRef = useRef(null);
  const obsRef = useRef(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  useEffect(() => {
    const el = divRef.current;
    if (!el) return;
    const applySize = img => {
      img.removeAttribute('width'); img.removeAttribute('height');
      img.style.width = '25%'; img.style.height = 'auto';
      img.style.maxWidth = '25%'; img.style.cursor = 'zoom-in';
    };
    const onDbl = e => { if (e.target.tagName === 'IMG') setPreviewSrc(e.target.src); };
    el.addEventListener('dblclick', onDbl);
    obsRef.current = new MutationObserver(mutations => {
      mutations.forEach(m => {
        m.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'IMG') applySize(node);
          node.querySelectorAll?.('img').forEach(applySize);
        });
        if (m.type === 'attributes' && m.target.tagName === 'IMG') applySize(m.target);
      });
    });
    obsRef.current.observe(el, { childList:true, subtree:true, attributes:true, attributeFilter:['width','height','style'] });
    return () => { obsRef.current?.disconnect(); el.removeEventListener('dblclick', onDbl); };
  }, []);
  return (
    <>
      <div ref={divRef} className="border border-gray-200 rounded-lg overflow-hidden">{editorNode}</div>
      {previewSrc && (
        <div onClick={() => setPreviewSrc(null)}
          className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-sm flex items-center justify-center cursor-zoom-out">
          <button onClick={() => setPreviewSrc(null)}
            className="absolute top-4 right-5 w-9 h-9 rounded-full bg-white/20 border-none flex items-center justify-center cursor-pointer text-white">
            <X size={18}/>
          </button>
          <img src={previewSrc} alt="preview" onClick={e => e.stopPropagation()}
            className="max-w-[92vw] max-h-[88vh] rounded-xl shadow-2xl cursor-default"/>
        </div>
      )}
    </>
  );
}
import CreateSubtaskModal from "./CreateSubtaskModal";
import EditTaskModal      from "./EditTaskModal";

/* ══ HELPERS ══ */
const fmtDate = (v) => {
  if (!v || typeof v === "object") return "—";
  try {
    const dt = new Date(v);
    if (isNaN(dt.getTime())) return String(v);
    const dd   = String(dt.getDate()).padStart(2, "0");
    const mm   = String(dt.getMonth() + 1).padStart(2, "0");
    const h24  = dt.getHours();
    const h12  = h24 % 12 || 12;
    const ampm = h24 >= 12 ? "PM" : "AM";
    const min  = String(dt.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${dt.getFullYear()} ${String(h12).padStart(2,"0")}:${min} ${ampm}`;
  } catch { return String(v); }
};

const fmtLocalNaive = (v) => {
  if (!v || typeof v === "object") return "—";
  try {
    const dt = new Date(v);
    if (isNaN(dt.getTime())) return String(v);
    const dd   = String(dt.getUTCDate()).padStart(2, "0");
    const mm   = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const h24  = dt.getUTCHours();
    const h12  = h24 % 12 || 12;
    const ampm = h24 >= 12 ? "PM" : "AM";
    const min  = String(dt.getUTCMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${dt.getUTCFullYear()} ${String(h12).padStart(2,"0")}:${min} ${ampm}`;
  } catch { return String(v); }
};

const fmtUtcStr = (s) => {
  if (!s) return "";
  try {
    const d = new Date(String(s).trim() + " UTC");
    if (isNaN(d.getTime())) return String(s);
    const dd   = String(d.getDate()).padStart(2, "0");
    const mm   = String(d.getMonth() + 1).padStart(2, "0");
    const h24  = d.getHours();
    const h12  = h24 % 12 || 12;
    const ampm = h24 >= 12 ? "PM" : "AM";
    const min  = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${d.getFullYear()} ${String(h12).padStart(2,"0")}:${min} ${ampm}`;
  } catch { return String(s); }
};

const colorMap  = ["#6366F1","#8B5CF6","#EC4899","#F59E0B","#10B981","#3B82F6"];
const initColor = (n = "") => colorMap[(n || "").charCodeAt(0) % colorMap.length];

function Av({ name = "?", size = 28 }) {
  const ini = (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span
      className="inline-flex items-center justify-center rounded-full text-white font-semibold shrink-0"
      style={{ width:size, height:size, background:initColor(name), fontSize:size*0.35 }}
    >{ini}</span>
  );
}

const STATUS_CFG = {
  "open":             { label:"Open",           bg:"#EEEDFE", color:"#3C3489", dot:"#534AB7" },
  "in progress":      { label:"In Progress",    bg:"#D1FAE5", color:"#065F46", dot:"#10B981" },
  "in-progress":      { label:"In Progress",    bg:"#D1FAE5", color:"#065F46", dot:"#10B981" },
  "on hold":          { label:"On Hold",        bg:"#FCE7F3", color:"#9D174D", dot:"#EC4899" },
  "on-hold":          { label:"On Hold",        bg:"#FCE7F3", color:"#9D174D", dot:"#EC4899" },
  "completed":        { label:"Complete",       bg:"#D1FAE5", color:"#065F46", dot:"#10B981" },
  "approval pending": { label:"Approval Await", bg:"#FEF3C7", color:"#92400E", dot:"#F59E0B" },
  "closed":           { label:"Closed",         bg:"#F3F4F6", color:"#374151", dot:"#6B7280" },
  "pending":          { label:"Pending",        bg:"#F1F5F9", color:"#475569", dot:"#94A3B8" },
  "overdue":          { label:"Overdue",        bg:"#FEE2E2", color:"#991B1B", dot:"#EF4444" },
  "complete":         { label:"Complete",       bg:"#D1FAE5", color:"#065F46", dot:"#10B981" },
  "cancelled":        { label:"Cancelled",      bg:"#F3F4F6", color:"#374151", dot:"#6B7280" },
};

const PRIORITY_CFG = {
  "URGENT": { bg:"#FEE2E2", color:"#991B1B" },
  "HIGH":   { bg:"#FFEDD5", color:"#9A3412" },
  "MEDIUM": { bg:"#FEF9C3", color:"#854D0E" },
  "LOW":    { bg:"#DCFCE7", color:"#166534" },
};

const ALL_STATUSES = [
  { value:"open",             label:"Open" },
  { value:"in progress",      label:"In Progress" },
  { value:"on hold",          label:"On Hold" },
  { value:"completed",        label:"Complete" },
  { value:"approval pending", label:"Approval Await" },
  { value:"closed",           label:"Closed" },
];

function Toast({ msg, type = "success" }) {
  return (
    <div className={`fixed bottom-6 right-6 z-[9999] text-white rounded-xl px-[18px] py-[10px] text-[13px] font-semibold shadow-[0_4px_16px_rgba(0,0,0,.2)] ${
      type === "error" ? "bg-[#EF4444]" : "bg-[#10B981]"
    }`}>{msg}</div>
  );
}

const LabelDark = ({ txt }) => (
  <div className="text-[10px] text-[#94A3B8] font-semibold uppercase tracking-[.07em] mb-[5px]">{txt}</div>
);

function SideBtn({ icon, label, onClick, bg="#fff", color="#1E293B", highlight=false }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] cursor-pointer w-full text-left font-medium font-[inherit] border-[0.5px] transition-colors ${
        highlight
          ? "bg-[#EEEDFE] text-[#3C3489] border-[#AFA9EC] hover:bg-[#E0DFFE]"
          : "border-[#E2E8F0] hover:bg-[#F1F5F9]"
      }`}
      style={!highlight ? { background: bg, color } : undefined}
    >
      <span className={`shrink-0 ${highlight ? "text-[#3C3489]" : "text-[rgb(124,132,207)]"}`}>{icon}</span>
      {label}
    </button>
  );
}

const nameList = (v) => {
  if (!v) return "—";
  if (Array.isArray(v)) return v.map(i => typeof i==="string"?i:String(i)).join(", ") || "—";
  if (typeof v === "object") return "—";
  return String(v);
};

/* ══ LIGHTBOX ══ */
function Lightbox({ src, onClose }) {
  if (!src) return null;
  const download = () => {
    const a = document.createElement("a");
    a.href = src; a.download = src.split("/").pop() || "image";
    a.target = "_blank"; a.click();
  };
  return createPortal(
    <div onClick={onClose}
      className="fixed inset-0 z-[999999] bg-black/[.88] flex items-center justify-center flex-col gap-3">
      <div onClick={e=>e.stopPropagation()}
        className="flex items-center gap-3 px-4 py-2 bg-white/[.08] rounded-xl">
        <button onClick={download}
          className="flex items-center gap-1.5 px-[14px] py-[6px] bg-[rgb(124,132,207)] text-white border-none rounded-lg text-[13px] font-semibold cursor-pointer font-[inherit]">
          <Download size={13}/> Download
        </button>
        <button onClick={onClose}
          className="flex items-center gap-1 px-[14px] py-[6px] bg-[rgba(255,255,255,.12)] text-white border-none rounded-lg text-[13px] font-medium cursor-pointer font-[inherit]">
          <X size={13}/> Close
        </button>
      </div>
      <img src={src} alt="preview" onClick={e=>e.stopPropagation()}
        className="max-w-[92vw] max-h-[80vh] rounded-[10px] shadow-[0_8px_40px_rgba(0,0,0,.5)] object-contain"/>
    </div>,
    document.body
  );
}

/* ══ DnD FILE UPLOAD MODAL ══ */
function AttachUploadModal({ onClose, onUpload }) {
  const [dragging,  setDragging]  = useState(false);
  const [staged,    setStaged]    = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const addFiles = (fileList) => {
    const arr = Array.from(fileList);
    setStaged(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      return [...prev, ...arr.filter(f => !existing.has(f.name + f.size))];
    });
  };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    addFiles(e.dataTransfer.files);
  }, []);

  const fmtSize = (b) => b > 1024*1024 ? `${(b/1024/1024).toFixed(1)} MB` : `${(b/1024).toFixed(0)} KB`;

  const handleUpload = async () => {
    if (!staged.length) return;
    setUploading(true);
    await onUpload(staged);
    setUploading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[1600] bg-black/[.45] flex items-center justify-center"
      onClick={onClose}>
      <div onClick={e=>e.stopPropagation()}
        className="bg-white rounded-2xl w-[480px] max-w-[95vw] shadow-[0_20px_60px_rgba(0,0,0,.2)] overflow-hidden">
        <div className="flex items-center justify-between px-[18px] py-[14px] border-b-[0.5px] border-[#E8EAF0]">
          <div className="flex items-center gap-2">
            <Upload size={16} color="rgb(124,132,207)"/>
            <span className="text-[14px] font-bold text-[#1E293B]">Upload Files</span>
          </div>
          <button onClick={onClose} className="bg-transparent border-none cursor-pointer">
            <X size={16} color="#64748B"/>
          </button>
        </div>
        <div className="p-[18px]">
          <div onDrop={onDrop}
            onDragOver={e=>{ e.preventDefault(); setDragging(true); }}
            onDragLeave={()=>setDragging(false)}
            onClick={() => inputRef.current.click()}
            className={`rounded-xl py-8 px-5 text-center cursor-pointer transition-all border-2 border-dashed ${
              dragging ? "border-[rgb(124,132,207)] bg-[#EEEDFE20]" : "border-[#D1D5DB] bg-[#FAFBFE]"
            }`}>
            <Upload size={28} color={dragging?"rgb(124,132,207)":"#94A3B8"}
              className="block mx-auto mb-[10px]"/>
            <p className={`text-[13px] m-0 font-medium ${dragging ? "text-[rgb(124,132,207)]" : "text-[#64748B]"}`}>
              {dragging ? "Drop files here!" : "Drag & drop files here"}
            </p>
            <p className="text-[11px] text-[#94A3B8] mt-1 mb-0">
              or <span className="text-[rgb(124,132,207)] font-semibold">click to browse</span>
            </p>
            <p className="text-[10px] text-[#94A3B8] mt-1 mb-0">.jpg, .jpeg, .png, .pdf, .csv, .xlsx, .docx, .doc, .xls, .ppt, .pptx, .txt, .webp · Max 5MB per file</p>
          </div>
          <input ref={inputRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" className="hidden"
            onChange={e => addFiles(e.target.files)}/>
          {staged.length > 0 && (
            <div className="mt-[14px] flex flex-col gap-2">
              {staged.map((f,i) => (
                <div key={i} className="flex items-center gap-[10px] px-3 py-2 bg-[#F8FAFC] rounded-lg border-[0.5px] border-[#E8EAF0]">
                  <FileText size={16} color="rgb(124,132,207)" className="shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium text-[#1E293B] overflow-hidden text-ellipsis whitespace-nowrap">{f.name}</div>
                    <div className="text-[11px] text-[#94A3B8]">{fmtSize(f.size)}</div>
                  </div>
                  <button onClick={() => setStaged(p=>p.filter((_,j)=>j!==i))}
                    className="bg-[#FEE2E2] border-none rounded-[6px] w-[22px] h-[22px] flex items-center justify-center cursor-pointer shrink-0">
                    <X size={10} color="#EF4444"/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-[10px] px-[18px] py-3 border-t-[0.5px] border-[#E8EAF0] bg-[#F8FAFC]">
          <button onClick={onClose}
            className="flex-1 p-[9px] rounded-[10px] border-[0.5px] border-[#E8EAF0] bg-white text-[13px] font-semibold cursor-pointer text-[#64748B] font-[inherit]">
            Cancel
          </button>
          <button onClick={handleUpload} disabled={!staged.length||uploading}
            className={`flex-1 p-[9px] rounded-[10px] border-none text-[13px] font-semibold font-[inherit] flex items-center justify-center gap-1.5 ${
              staged.length ? "bg-[rgb(124,132,207)] cursor-pointer text-white" : "bg-[#E8EAF0] cursor-not-allowed text-[#94A3B8]"
            }`}>
            {uploading?<><Loader size={13}/> Uploading...</>:<><Upload size={13}/> Upload {staged.length>0?`(${staged.length})`:""}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══ FORWARD MODAL ══ */
function ForwardModal({ taskId, onClose, onSuccess, showToast }) {
  const [results,  setResults]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [selected, setSelected] = useState([]);
  const [search,   setSearch]   = useState("");
  const [posting,  setPosting]  = useState(false);
  const debRef   = useRef(null);
  const abortRef = useRef(null);

  const doSearch = useCallback((term) => {
    clearTimeout(debRef.current);
    abortRef.current?.abort();
    debRef.current = setTimeout(async () => {
      abortRef.current = new AbortController();
      setLoading(true);
      try {
        const res = await axiosInstance.get("/task_assignment/get_users_all", {
          params: { q: term || "" },
          signal: abortRef.current.signal,
        });
        const data = Array.isArray(res.data) ? res.data : [];
        setResults(data.map(u => ({ value: String(u.staffid), label: u.display_name || u.alias_name || String(u.staffid) })));
      } catch (e) {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        setResults([]);
      } finally { setLoading(false); }
    }, 300);
  }, []);

  useEffect(() => { doSearch(""); }, []);

  const isSelected = (val) => selected.some(s=>s.value===val);

  const toggle = (val, label) => {
    if (isSelected(val)) setSelected(p=>p.filter(s=>s.value!==val));
    else setSelected(p=>[...p,{value:val,label}]);
  };

  const submit = async () => {
    if (!selected.length) return;
    setPosting(true);
    try {
      const res = await axiosInstance.post("/task_assignment/forward_task", {
        task_id: taskId, user_ids: selected.map(s=>s.value),
      });
      if (res.data?.success) { showToast("Task forwarded successfully"); onSuccess(); onClose(); }
      else showToast(res.data?.message||"Failed to forward task","error");
    } catch { showToast("Failed to forward task. Please try again.","error"); }
    finally { setPosting(false); }
  };

  return (
    <div className="fixed inset-0 z-[1600] bg-black/40 flex items-center justify-center" onClick={()=>onClose()}>
      <div onClick={e=>e.stopPropagation()}
        className="bg-white rounded-2xl p-6 w-[380px] max-w-[90vw] shadow-[0_20px_60px_rgba(0,0,0,.2)]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[15px] font-bold text-[#1E293B] m-0">Forward To</h3>
          <button onClick={onClose} className="bg-transparent border-none cursor-pointer"><X size={16} color="#64748B"/></button>
        </div>
        <input value={search} onChange={e=>{ setSearch(e.target.value); doSearch(e.target.value); }}
          autoFocus placeholder="Search users..."
          className="w-full px-3 py-2 rounded-lg border-[0.5px] border-[#E8EAF0] text-[13px] outline-none box-border font-[inherit] mb-[10px]"/>
        {selected.length>0 && (
          <div className="flex flex-wrap gap-[6px] mb-[10px]">
            {selected.map(s=>(
              <span key={s.value} className="flex items-center gap-1 bg-[#EEEDFE] text-[#3C3489] text-[11px] font-semibold px-2 py-[3px] rounded-full border-[0.5px] border-[#AFA9EC]">
                {s.label}
                <button onClick={()=>toggle(s.value,s.label)}
                  className="bg-transparent border-none cursor-pointer text-[#3C3489] text-sm leading-none p-0">×</button>
              </span>
            ))}
          </div>
        )}
        <div className="max-h-[200px] overflow-y-auto border-[0.5px] border-[#E8EAF0] rounded-[10px]">
          {loading ? (
            <div className="p-4 text-center"><Loader size={18} color="rgb(124,132,207)"/></div>
          ) : results.map(u=>{
            const sel = isSelected(u.value);
            return (
              <div key={u.value} onClick={()=>toggle(u.value,u.label)}
                className={`flex items-center gap-[10px] px-3 py-2 cursor-pointer border-b-[0.5px] border-[#F1F5F9] ${
                  sel ? "bg-[#EEEDFE]" : "bg-white hover:bg-[#F8FAFC]"
                }`}>
                <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 border-2 ${
                  sel ? "border-[rgb(124,132,207)] bg-[rgb(124,132,207)]" : "border-[#D1D5DB] bg-white"
                }`}>
                  {sel &&<Check size={9} color="#fff"/>}
                </div>
                <span className="text-[13px] text-[#374151]">{u.label}</span>
              </div>
            );
          })}
        </div>
        <div className="flex gap-[10px] mt-4">
          <button onClick={onClose}
            className="flex-1 p-[9px] rounded-[10px] border-[0.5px] border-[#E8EAF0] bg-white text-[13px] font-semibold cursor-pointer text-[#64748B] font-[inherit]">Cancel</button>
          <button onClick={submit} disabled={!selected.length||posting}
            className={`flex-1 p-[9px] rounded-[10px] border-none text-[13px] font-semibold cursor-pointer font-[inherit] ${
              selected.length ? "bg-[rgb(124,132,207)] text-white" : "bg-[#E8EAF0] text-[#94A3B8]"
            }`}>
            {posting?<Loader size={12}/>:"Forward"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══ EXT DEADLINE MODAL ══ */
function ExtDeadlineModal({ taskId, onClose, onSuccess, showToast }) {
  const [newDeadline, setNewDeadline] = useState("");
  const [posting,     setPosting]     = useState(false);

  const submit = async () => {
    if (!newDeadline) return;
    setPosting(true);
    try {
      const res = await axiosInstance.post(`/task_assignment/extend_deadline/${taskId}`, {
        new_deadline: newDeadline.replace("T"," ")
      });
      if (res.data?.success!==false) { showToast("Deadline change request sent to Accountable person for approval"); onSuccess(); onClose(); }
      else showToast(res.data?.message||"Failed to extend deadline","error");
    } catch { showToast("Failed to extend deadline. Please try again.","error"); }
    finally { setPosting(false); }
  };

  return (
    <div className="fixed inset-0 z-[1600] bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()}
        className="bg-white rounded-2xl p-6 w-[340px] max-w-[90vw] shadow-[0_20px_60px_rgba(0,0,0,.2)]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[15px] font-bold text-[#1E293B] m-0">Extended Deadline</h3>
          <button onClick={onClose} className="bg-transparent border-none cursor-pointer"><X size={16} color="#64748B"/></button>
        </div>
        <label className="text-[12px] font-semibold text-[#64748B] block mb-[6px]">New Deadline</label>
        <div className="flex gap-[6px]">
          <input type="date" value={newDeadline ? newDeadline.split('T')[0] : ''}
            onChange={e => setNewDeadline(e.target.value ? `${e.target.value}T${newDeadline.split('T')[1]||'00:00'}` : '')}
            className="flex-[3] px-3 py-2 rounded-lg border-[0.5px] border-[#E8EAF0] text-[13px] outline-none box-border font-[inherit]"/>
          <input type="time" value={newDeadline ? (newDeadline.split('T')[1]||'') : ''}
            onChange={e => { const d=newDeadline.split('T')[0]; if(d) setNewDeadline(`${d}T${e.target.value}`); }}
            className="flex-[2] px-3 py-2 rounded-lg border-[0.5px] border-[#E8EAF0] text-[13px] outline-none box-border font-[inherit]"/>
        </div>
        <p className="text-[11px] text-[#94A3B8] mt-2 mb-0">
          After submission, accountable person will need to approve the new deadline.
        </p>
        <div className="flex gap-[10px] mt-4">
          <button onClick={onClose}
            className="flex-1 p-[9px] rounded-[10px] border-[0.5px] border-[#E8EAF0] bg-white text-[13px] font-semibold cursor-pointer text-[#64748B] font-[inherit]">Cancel</button>
          <button onClick={submit} disabled={!newDeadline||posting}
            className={`flex-1 p-[9px] rounded-[10px] border-none text-[13px] font-semibold cursor-pointer font-[inherit] ${
              newDeadline ? "bg-[rgb(124,132,207)] text-white" : "bg-[#E8EAF0] text-[#94A3B8]"
            }`}>
            {posting?<Loader size={12}/>:"Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══ WORKSPACE CACHE ══ */
let _wsCache = null;
async function resolveWorkspaceName(productCode) {
  if (!productCode) return null;
  if (!_wsCache) {
    try {
      const res = await axiosInstance.get("/task_assignment/get_product_codes");
      const arr = Array.isArray(res.data?.product_codes)?res.data.product_codes
                : Array.isArray(res.data)?res.data:[];
      _wsCache = {};
      arr.forEach(item => {
        if (typeof item==="object") { const key=Object.keys(item)[0]; _wsCache[String(key)]=item[key]; }
      });
    } catch { _wsCache = {}; }
  }
  return _wsCache[String(productCode)] || null;
}

/* ══ PRIORITY DROPDOWN ══ */
const PRIORITY_COLORS = {
  URGENT: { bg:"#FEF2F2", color:"#DC2626", border:"#FECACA" },
  HIGH:   { bg:"#FFF7ED", color:"#D97706", border:"#FDE68A" },
  MEDIUM: { bg:"#FEF9C3", color:"#CA8A04", border:"#FDE68A" },
  LOW:    { bg:"#DCFCE7", color:"#16A34A", border:"#BBF7D0" },
};
const PRIORITY_DEFAULT_COLOR = { bg:"#F1F5F9", color:"#475569", border:"#CBD5E1" };
function PriorityPickerDropdown({ value, onChange, error, options = [] }) {
  const [open, setOpen] = useState(false);
  const [dropRect, setDropRect] = useState(null);
  const triggerRef = useRef(null);
  const wrapRef    = useRef(null);

  useEffect(() => {
    const fn = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const handleOpen = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setDropRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen(p => !p);
  };

  const cfg = value ? (PRIORITY_COLORS[value.toUpperCase()] || PRIORITY_DEFAULT_COLOR) : {};
  return (
    <div>
      <div
        ref={triggerRef}
        onClick={handleOpen}
        className={`flex items-center justify-between px-3 py-[9px] rounded-lg bg-white cursor-pointer transition-all min-h-[38px] border ${
          error ? "border-[#EF4444]" : open ? "border-[#7C3AED] shadow-[0_0_0_3px_rgba(124,58,237,.08)]" : "border-[#E2E8F0]"
        }`}
      >
        {value ? (
          <span
            className="inline-flex items-center gap-[6px] text-[12px] font-semibold px-[10px] py-[3px] rounded-full border-[1.5px]"
            style={{ background:cfg.bg, color:cfg.color, borderColor:cfg.border }}
          >
            <span className="w-[6px] h-[6px] rounded-full shrink-0" style={{ background:cfg.color }}/>
            {value}
          </span>
        ) : (
          <span className="text-[#94A3B8] text-[13px]">Select priority</span>
        )}
        <ChevronDown size={14} color="#94A3B8"
          className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"}`}/>
      </div>
      {open && dropRect && (
        <div ref={wrapRef}
          className="fixed bg-white border border-[#E2E8F0] rounded-xl shadow-[0_8px_32px_rgba(0,0,0,.15)] overflow-hidden z-[99999]"
          style={{ top:dropRect.top, left:dropRect.left, width:dropRect.width }}>
          {options.map(p => {
            const c = PRIORITY_COLORS[p?.toUpperCase()] || PRIORITY_DEFAULT_COLOR;
            const sel = value === p;
            return (
              <div key={p} onClick={() => { onChange(p); setOpen(false); }}
                className={`flex items-center gap-[10px] px-3 py-[10px] cursor-pointer transition-colors ${
                  sel ? "bg-[#F5F3FF]" : "bg-white hover:bg-[#F8FAFC]"
                }`}>
                <div className={`w-4 h-4 rounded-full shrink-0 flex items-center justify-center border-2 ${
                  sel ? "border-[#7C3AED] bg-[#7C3AED]" : "border-[#D1D5DB] bg-white"
                }`}>
                  {sel && <div className="w-[5px] h-[5px] rounded-full bg-white"/>}
                </div>
                <span
                  className="inline-flex items-center gap-[6px] text-[12px] font-semibold px-[10px] py-[3px] rounded-full border-[1.5px]"
                  style={{ background:c.bg, color:c.color, borderColor:c.border }}
                >
                  <span className="w-[6px] h-[6px] rounded-full" style={{ background:c.color }}/>
                  {p}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══ MAIN ══ */
export default function TaskDetailPanel({ taskId, onClose, isSubtaskView=false, onAction=()=>{}, onTaskUpdate=()=>{} }) {
  const open = !!taskId;

  const [task,            setTask]            = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [toast,           setToast]           = useState(null);
  const [activeTab,       setActiveTab]       = useState("desc");
  const [commentText,     setCommentText]     = useState("");
  const [postingCmt,      setPostingCmt]      = useState(false);
  const [mentionQuery,    setMentionQuery]    = useState(null);
  const [mentionIndex,    setMentionIndex]    = useState(0);
  const [dropdownPos,     setDropdownPos]     = useState({ top: 0, left: 0, width: 300 });
  const [descExpanded,    setDescExpanded]    = useState(false);
  const [isEditingDesc,   setIsEditingDesc]   = useState(false);
  const [editDescText,    setEditDescText]    = useState("");
  const [savingDesc,      setSavingDesc]      = useState(false);
  const [lightboxSrc,     setLightboxSrc]     = useState(null);
  const [workspaceName,   setWorkspaceName]   = useState(null);
  const [showUpload,      setShowUpload]      = useState(false);
  const [showForward,     setShowForward]     = useState(false);
  const [showExtDeadline, setShowExtDeadline] = useState(false);
  const [showCreateSubtask, setShowCreateSubtask] = useState(false);
  const [showEditTask,    setShowEditTask]    = useState(false);
  const [fileToDelete,    setFileToDelete]    = useState(null);
  const [deletingFile,    setDeletingFile]    = useState(false);
  const [showCloseTaskModal, setShowCloseTaskModal] = useState(false);
  const [closeTaskReason,    setCloseTaskReason]    = useState("");
  const [closeTaskReasonErr, setCloseTaskReasonErr] = useState(false);
  const [closingTask,        setClosingTask]        = useState(false);
  const [updatingStatus,     setUpdatingStatus]     = useState(false);
  const [showReopenModal,    setShowReopenModal]    = useState(false);
  const [reopenReason,       setReopenReason]       = useState("");
  const [reopenReasonErr,    setReopenReasonErr]    = useState(false);
  const [reopenPriority,     setReopenPriority]     = useState("MEDIUM");
  const [reopenPriorityOpts, setReopenPriorityOpts] = useState(["URGENT","HIGH","MEDIUM","LOW"]);
  const [reopenDeadline,     setReopenDeadline]     = useState("");
  const [reopenDeadlineErr,  setReopenDeadlineErr]  = useState(false);
  const [reopeningTask,      setReopeningTask]      = useState(false);
  const [subDetailId,    setSubDetailId]    = useState(null);
  const [isProductOwner, setIsProductOwner] = useState(false);

  const cmtEditorRef    = useRef(null);
  const cmtWrapRef      = useRef(null);
  const mentionStateRef = useRef({ query: null, index: 0 });
  const raciMembersRef  = useRef([]);
  const insertMentionFnRef = useRef(null);
  // Exact Quill document index of the "@" that opened the mention dropdown —
  // captured once at type-time, reused as-is on click. Re-deriving this by
  // walking back through quill.getText() at click-time breaks whenever an
  // embedded image precedes the mention (see CommentPanel.js for the full
  // writeup of why the two indices don't line up).
  const mentionAtIndexRef = useRef(null);

  const showToast = (msg, type="success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Fetch workspaces where current user is product owner (workspace-specific)
  const [productOwnerWorkspaces, setProductOwnerWorkspaces] = useState([]);
  useEffect(() => {
    axiosInstance.get("/task_assignment/get_product_owner_workspaces")
      .then(res => {
        const list = Array.isArray(res.data?.workspaces) ? res.data.workspaces : [];
        setProductOwnerWorkspaces(list.map(String));
        setIsProductOwner(list.length > 0);
      })
      .catch(() => {});
  }, []);

  const refreshTask = useCallback(async () => {
    if (!taskId) return;
    try {
      const res = await axiosInstance.get(`/task_assignment/get_task_details/${taskId}`);
      if (!res.data?.error) setTask(res.data);
    } catch {}
  }, [taskId]);

  useEffect(() => {
    if (!taskId) { setTask(null); return; }
    setLoading(true); setError(null); setTask(null);
    setCommentText(""); setActiveTab("desc");
    setDescExpanded(false); setIsEditingDesc(false);
    setEditDescText(""); setLightboxSrc(null);
    setWorkspaceName(null); setShowUpload(false);
    setShowForward(false); setShowExtDeadline(false);
    setShowCreateSubtask(false); setShowEditTask(false);
    setSubDetailId(null);

    axiosInstance.get(`/task_assignment/get_task_details/${taskId}`)
      .then(async res => {
        if (res.data?.error) { setError(res.data.error); }
        else {
          setTask(res.data);
          if (!res.data.product_name && res.data.product_code) {
            const name = await resolveWorkspaceName(res.data.product_code);
            if (name) setWorkspaceName(name);
          }
        }
      })
      .catch(err => setError(err?.response?.data?.error||"Failed to load task"))
      .finally(() => setLoading(false));
  }, [taskId]);

  useEffect(() => {
    if (!task || !task.repeat) return;
    if (["approval pending","on hold","on-hold"].includes((task.task_status||"").toLowerCase())) return;
    const occs = Array.isArray(task.occurrence_dates) ? task.occurrence_dates : [];
    const cur =
      occs.find(o => typeof o === "object" && o !== null && ["in progress","in-progress","approval pending","on hold","on-hold"].includes((o.status||"").toLowerCase())) ||
      occs.find(o => typeof o === "object" && o !== null && (o.status||"").toLowerCase() === "pending");
    if (!cur) return;
    const rawSt = (cur.status||"").toLowerCase();
    const curStartDate = cur.start_date || cur.start_date_utc || null;
    const isFutureOcc = curStartDate && new Date(curStartDate) > new Date();
    const effSt = rawSt === "pending" ? (isFutureOcc ? "completed" : "open") : rawSt;
    const effDl = cur.completion_sla || cur.completion_sla_utc || task.completion_sla;
    if (effSt !== (task.task_status||"").toLowerCase() || effDl !== task.completion_sla) {
      onTaskUpdate({ _id: taskId, task_status: effSt, completion_sla: effDl });
    }
  }, [task]); // eslint-disable-line

  const changeStatus = async (newStatus) => {
    if (["completed","approval pending","closed"].includes(newStatus) && !isSubtask) {
      if (subtasks.length > 0) {
        const incomplete = subtasks.filter(s =>
          !["completed","complete","closed"].includes((s.task_status||"").toLowerCase())
        );
        if (incomplete.length > 0) {
          showToast(`Cannot ${newStatus === "closed" ? "close" : "complete"} task — ${incomplete.length} subtask(s) are still open.`, "error");
          return;
        }
      }
    }
    setUpdatingStatus(true);
    try {
      const res = await axiosInstance.post(`/task_assignment/update_status/${taskId}`, {
        new_status: newStatus,
        client_local_time: new Date().toLocaleString('sv-SE', {timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone}),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      if (res.data?.success) {
        setTask(prev => ({ ...prev, task_status: res.data?.new_status || newStatus }));
        await refreshTask();
        showToast(`Status updated to ${newStatus}`);
        onAction();
      } else {
        showToast(res.data?.message||"Failed to update status","error");
      }
    } catch { showToast("Failed to update status. Please try again.","error"); }
    finally { setUpdatingStatus(false); }
  };

  const raciMembers = (() => {
    if (!task) return [];
    const seen = new Set();
    const add = (val) => {
      if (Array.isArray(val)) val.forEach(n => n && seen.add(String(n).trim()));
      else if (val) seen.add(String(val).trim());
    };
    add(task.task_created_by_name || task.task_created_by);
    add(task.assigned_to_name);
    add(task.observer_to_name);
    add(task.informer_to_name);
    return [...seen].filter(Boolean);
  })();

  mentionStateRef.current = { query: mentionQuery, index: mentionIndex };
  raciMembersRef.current  = raciMembers;

  const filteredMentions = mentionQuery !== null
    ? raciMembers.filter(m => m.toLowerCase().includes((mentionQuery || "").toLowerCase()))
    : [];

  // A comment is only "empty" if it has no visible text AND no image embed —
  // stripping tags to check for text would otherwise also strip <img>, making
  // an image-only comment (a pasted screenshot) look empty and blocking it
  // from ever being sent.
  const isCommentEmpty = (val) => {
    if (!val) return true;
    if (val.replace(/<[^>]*>/g, "").trim()) return false;
    return !/<img[\s/>]/i.test(val);
  };

  const postComment = async () => {
    if (isCommentEmpty(commentText)) return;
    setPostingCmt(true);
    try {
      await axiosInstance.post(`/task_assignment/add_comment/${taskId}`, { comment:commentText.trim() });
      showToast("Comment posted successfully");
      setCommentText("");
      // setCommentText("") alone doesn't visually clear the box — the Editor
      // seeds its Quill content once from `value` and doesn't re-sync on
      // every change, so the old text/screenshot stays on screen (and the
      // Add Comment button looks "stuck") unless we clear the actual Quill
      // instance directly.
      cmtEditorRef.current?.getQuill()?.setText("");
      setMentionQuery(null);
      await refreshTask();
      onAction();
    } catch { showToast("Failed to post comment. Please try again.","error"); }
    finally { setPostingCmt(false); }
  };

  const handleCommentChange = (e) => {
    const quill = cmtEditorRef.current?.getQuill();
    // Shrink any pasted/attached image to 25% width right now, synchronously,
    // before reading the HTML that gets saved into commentText and posted to
    // the backend. Doing this via a MutationObserver after the fact is a
    // race — e.htmlValue here is a snapshot taken before that observer's
    // callback runs, so the comment gets saved (and later re-rendered in the
    // history) at full size regardless of what the observer does visually.
    if (quill) {
      quill.root.querySelectorAll("img").forEach(img => {
        if (img.style.width !== "25%") {
          img.style.width    = "25%";
          img.style.maxWidth = "25%";
          img.style.height   = "auto";
        }
      });
    }
    const html = quill ? quill.root.innerHTML : (e.htmlValue || "");
    setCommentText(html);
    const plain = (e.textValue || "").replace(/\n+$/, "");
    const match = plain.match(/@([^\s@]*)$/);
    if (match) {
      // Capture the real Quill index of "@" right now, while the editor
      // definitely has focus and an accurate selection. match[0] ("@"+query)
      // is plain ASCII typed just now, so its length maps 1:1 onto Quill's
      // index regardless of any images earlier in the document.
      const quill = cmtEditorRef.current?.getQuill();
      const sel = quill?.getSelection();
      mentionAtIndexRef.current = sel ? sel.index - match[0].length : null;
      setMentionQuery(match[1]);
      setMentionIndex(0);
      if (cmtWrapRef.current) {
        const rect = cmtWrapRef.current.getBoundingClientRect();
        const estimatedHeight = Math.min(raciMembers.length * 46 + 44, 280);
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < estimatedHeight + 8) {
          setDropdownPos({ bottom: window.innerHeight - rect.top + 4, top: null, left: rect.left, width: rect.width });
        } else {
          setDropdownPos({ top: rect.bottom + 4, bottom: null, left: rect.left, width: rect.width });
        }
      }
    } else {
      mentionAtIndexRef.current = null;
      setMentionQuery(null);
    }
  };

  const insertMention = (memberName) => {
    const quill = cmtEditorRef.current?.getQuill();
    const atIdx = mentionAtIndexRef.current;
    const query = mentionStateRef.current.query ?? "";

    if (!quill || atIdx == null || atIdx < 0) { setMentionQuery(null); return; }

    // Delete exactly "@" + whatever was typed since — no re-scanning of
    // quill.getText() needed, so an image embedded earlier in the comment
    // can't throw the offset off.
    const deleteLen = 1 + query.length;
    quill.deleteText(atIdx, deleteLen);
    quill.insertText(atIdx, `@${memberName}`, { color: "#7C3AED", bold: true });
    const afterMention = atIdx + memberName.length + 1;
    quill.setSelection(afterMention);
    quill.format("color", null);
    quill.format("bold", null);
    quill.insertText(afterMention, " ");
    quill.setSelection(afterMention + 1);
    setCommentText(quill.root.innerHTML);

    mentionAtIndexRef.current = null;
    setMentionQuery(null);
  };
  insertMentionFnRef.current = insertMention;

  useEffect(() => {
    const wrapper = cmtWrapRef.current;
    if (!wrapper) return;
    const handleCapture = (e) => {
      const { query, index } = mentionStateRef.current;
      if (query === null) return;
      const members = raciMembersRef.current.filter(m =>
        m.toLowerCase().includes((query || "").toLowerCase())
      );
      if (members.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault(); e.stopPropagation();
        setMentionIndex(i => (i + 1) % members.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault(); e.stopPropagation();
        setMentionIndex(i => (i - 1 + members.length) % members.length);
      } else if (e.key === "Enter") {
        const chosen = members[index];
        if (chosen) { e.preventDefault(); e.stopPropagation(); insertMentionFnRef.current?.(chosen); }
      } else if (e.key === "Escape") {
        e.preventDefault(); e.stopPropagation();
        mentionAtIndexRef.current = null;
        setMentionQuery(null);
      }
    };
    wrapper.addEventListener("keydown", handleCapture, true);
    return () => wrapper.removeEventListener("keydown", handleCapture, true);
  }, []);

  const handleUpload = async (fileList) => {
    const fd = new FormData();
    fileList.forEach(f => fd.append("file",f));
    fd.append("task_id", taskId);
    try {
      await axiosInstance.post("/task_assignment/file_upload", fd, {
        headers:{ "Content-Type":"multipart/form-data" }
      });
      const names = fileList.map(f => f.name).join(", ");
      await axiosInstance.post(`/api/task/${taskId}/activity_log`, {
        log_type: "File Uploaded",
        details:  `File(s) uploaded: ${names}`
      }).catch(() => {});
      showToast(`${fileList.length} file(s) uploaded successfully`);
      await refreshTask();
      onAction();
    } catch { showToast("File upload failed. Please try again.","error"); }
  };

  const deleteFile = async (orgFileName) => {
    try {
      await axiosInstance.post(`/task_assignment/delete_file`, { deleted_file_name: orgFileName });
      const displayName = orgFileName.split("/").pop();
      await axiosInstance.post(`/api/task/${taskId}/activity_log`, {
        log_type: "File Deleted",
        details:  `File deleted: ${displayName}`
      }).catch(() => {});
      setTask(prev => ({ ...prev, download_urls:prev.download_urls.filter(f=>f.org_file_name!==orgFileName) }));
      showToast("File deleted successfully");
      await refreshTask();
      onAction();
    } catch { showToast("Failed to delete file. Please try again.","error"); }
  };

  const confirmDeleteFile = async () => {
    if (!fileToDelete) return;
    setDeletingFile(true);
    await deleteFile(fileToDelete.org_file_name);
    setDeletingFile(false);
    setFileToDelete(null);
  };

  const handleDeadlineApproval = async (action) => {
    try {
      const res = await axiosInstance.post(`/task_assignment/approve_deadline_change/${taskId}`, { action });
      if (res.data?.success !== false) {
        showToast(action === "accept" ? "Deadline approved successfully" : "Deadline request rejected");
        await refreshTask();
        onAction();
      } else {
        showToast(res.data?.message || "Failed to process request", "error");
      }
    } catch { showToast("Failed to process request. Please try again.", "error"); }
  };

  const openReopenModal = () => {
    setReopenReason(""); setReopenReasonErr(false);
    setReopenPriority(task?.priority || "MEDIUM");
    setReopenDeadline(""); setReopenDeadlineErr(false);
    setShowReopenModal(true);
    axiosInstance.post("/task_assignment/task_priority")
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setReopenPriorityOpts(data.map(p => p.priority).filter(Boolean));
      })
      .catch(() => setReopenPriorityOpts(["URGENT","HIGH","MEDIUM","LOW"]));
  };

  const confirmReopen = async () => {
    let hasErr = false;
    if (!reopenReason.trim()) { setReopenReasonErr(true);   hasErr = true; }
    if (!reopenDeadline)      { setReopenDeadlineErr(true); hasErr = true; }
    if (hasErr) return;
    setReopeningTask(true);
    setShowReopenModal(false);
    try {
      const statusRes = await axiosInstance.post(`/task_assignment/update_status/${taskId}`, {
        new_status: "open",
        reopen_reason: reopenReason.trim(),
        client_local_time: new Date().toISOString().replace("T"," ").slice(0,19)
      });
      if (!statusRes.data?.success) {
        showToast(statusRes.data?.message || "Failed to reopen task", "error");
        setReopeningTask(false); return;
      }
      const fd = new FormData();
      fd.append("priority",      reopenPriority);
      fd.append("completionSLA", reopenDeadline.replace("T"," "));
      await axiosInstance.post(`/task_assignment/edit_task/${taskId}`, fd, {
        headers: { "Content-Type":"multipart/form-data" }
      }).catch(() => {});
      await axiosInstance.post(`/api/task/${taskId}/activity_log`, {
        log_type: "Task Reopened",
        details:  `Task reopened. Reason: ${reopenReason.trim()} | New Priority: ${reopenPriority} | New Deadline: ${reopenDeadline.replace("T"," ")}`
      }).catch(() => {});
      setTask(prev => ({ ...prev, task_status:"open", priority:reopenPriority, completion_sla:reopenDeadline.replace("T"," ") }));
      await refreshTask();
      showToast("Task reopened successfully");
      onAction();
    } catch { showToast("Failed to reopen task. Please try again.", "error"); }
    finally { setReopeningTask(false); }
  };

  const openCloseTaskModal = () => {
    setCloseTaskReason(""); setCloseTaskReasonErr(false); setShowCloseTaskModal(true);
  };

  const confirmCloseTask = async () => {
    if (!closeTaskReason.trim()) { setCloseTaskReasonErr(true); return; }
    setClosingTask(true);
    setShowCloseTaskModal(false);
    if (!isSubtask && subtasks.length > 0) {
      const incomplete = subtasks.filter(s =>
        !["completed","complete","closed"].includes((s.task_status||"").toLowerCase())
      );
      if (incomplete.length > 0) {
        showToast(`Cannot close task — ${incomplete.length} subtask(s) are still open.`, "error");
        setClosingTask(false); return;
      }
    }
    try {
      const res = await axiosInstance.post(`/task_assignment/update_status/${taskId}`, {
        new_status: "closed",
        close_reason: closeTaskReason.trim(),
        client_local_time: new Date().toISOString().replace("T"," ").slice(0,19)
      });
      if (res.data?.success) {
        setTask(prev => ({ ...prev, task_status: res.data?.new_status || "closed" }));
        await refreshTask();
        showToast("Task closed successfully");
        onAction();
      } else {
        showToast(res.data?.message || "Failed to close task", "error");
      }
    } catch { showToast("Failed to close task. Please try again.", "error"); }
    finally { setClosingTask(false); }
  };

  const saveDescription = async () => {
    if (!editDescText.trim()||editDescText==="<br>") {
      showToast("Description cannot be empty","error"); return;
    }
    setSavingDesc(true);
    try {
      await axiosInstance.post(`/task_assignment/update_task_description/${taskId}`, {
        detailed_description: editDescText
      });
      setTask(prev => ({ ...prev, detailed_description:editDescText }));
      setIsEditingDesc(false);
      showToast("Description updated successfully");
      onAction();
    } catch { showToast("Failed to update description. Please try again.","error"); }
    finally { setSavingDesc(false); }
  };

  /* ── Derived ── */
  const pc        = task?(PRIORITY_CFG[(task.priority||"").toUpperCase()]||PRIORITY_CFG["MEDIUM"]):{};
  const notes    = Array.isArray(task?.notes)           ? task.notes          : [];
  const actLog   = Array.isArray(task?.activity_log)    ? task.activity_log   : [];
  const subtasks = Array.isArray(task?.subtasks)        ? task.subtasks       : [];
  const files    = Array.isArray(task?.download_urls)   ? task.download_urls  : [];
  const occDates = Array.isArray(task?.occurrence_dates)? task.occurrence_dates: [];
  const overdueOccs = occDates.reduce((acc, occ, idx) => {
    if (typeof occ !== "object" || occ === null) return acc;
    const dl = occ.completion_sla || occ.completion_sla_utc || null;
    const st = (occ.status || "").toLowerCase();
    if (dl && new Date(dl) < new Date() && !["complete","completed","cancelled"].includes(st))
      acc.push(idx + 1);
    return acc;
  }, []);

  const _parentTaskStatus = (task?.task_status||"").toLowerCase();
  const _parentStatusOverrides = ["approval pending","on hold","on-hold"];
  const currentOcc = (task?.repeat && !_parentStatusOverrides.includes(_parentTaskStatus)) ? (
    occDates.find(o => typeof o === "object" && o !== null &&
      ["in progress","in-progress","approval pending","on hold","on-hold"].includes((o.status||"").toLowerCase())) ||
    occDates.find(o => typeof o === "object" && o !== null &&
      (o.status||"").toLowerCase() === "pending")
  ) : null;
  const _rawOccStatus = currentOcc ? (currentOcc.status||"").toLowerCase() : "";
  const _occStartDate = currentOcc ? (currentOcc.start_date || currentOcc.start_date_utc || null) : null;
  const _occIsFuture  = _occStartDate && new Date(_occStartDate) > new Date();
  const effectiveTaskStatus = _parentStatusOverrides.includes(_parentTaskStatus)
    ? _parentTaskStatus
    : currentOcc
      ? (_rawOccStatus === "pending" ? (_occIsFuture ? "completed" : "open") : _rawOccStatus)
      : _parentTaskStatus;
  const effectiveDeadline = currentOcc
    ? (currentOcc.completion_sla || currentOcc.completion_sla_utc || task?.completion_sla)
    : task?.completion_sla;

  const sc        = task?(STATUS_CFG[effectiveTaskStatus]||STATUS_CFG["open"]):{};
  const isOverdue = effectiveDeadline && new Date(effectiveDeadline)<new Date()
                    && !["completed","closed","complete"].includes(effectiveTaskStatus);
  const taskStatus = effectiveTaskStatus;
  const isOpen     = ["open","in progress","in-progress","on hold","on-hold"].includes(taskStatus);
  const showApproveReject = taskStatus==="approval pending" && task?.can_approve_reject;
  // Server computes these on raw (unencrypted) staff ids — task_created_by_id,
  // assigned_to, observer, informer are all AES-encrypted with a random IV per
  // call before reaching the client, so they can never be compared here directly.
  const isAccountable = !!task?.is_accountable;
  // Product owner check is workspace-specific (matches HTML version)
  const isOwnerOfThisWorkspace = productOwnerWorkspaces.includes(String(task?.product_code || ""));
  // Matches the backend's _check_edit_task_permission: Accountable (always),
  // or Product Owner of this workspace regardless of RACI role. Responsible/
  // Consulted/Informed alone (not Accountable, not PO) do NOT qualify.
  const canEditTask = isAccountable || isOwnerOfThisWorkspace;

  const isResponsible = !!task?.is_responsible;
  // Reopen hidden only for pure responsible (not accountable, not product owner of this workspace)
  const canReopen = isAccountable || isOwnerOfThisWorkspace || (!isResponsible);

  const showDeadlineApproval = task?.approval == false && !!task?.changed_deadline && isAccountable;

  // Consulted / Informed → read-only, no actions allowed — UNLESS they're
  // also a Product Owner of this workspace, who must always be able to act
  // on the task regardless of their RACI role.
  const isReadOnly = !isAccountable && !isResponsible && !isOwnerOfThisWorkspace && (!!task?.is_observer || !!task?.is_informer);

  const isSubtask = !!(task?.parent_id);

  const MAIN_TABS = [
    { id:"desc",       label:"Description",  icon:<AlignLeft size={13}/>,    badge:null           },
    { id:"attach",     label:"Attachments",  icon:<Paperclip size={13}/>,    badge:files.length    },
    { id:"raci",       label:"RACI Matrix",  icon:<Users size={13}/>,         badge:null           },
    { id:"subtasks",   label:"Subtasks",     icon:<CheckSquare size={13}/>,  badge:subtasks.length },
    { id:"occurrence", label:"Occurrences",  icon:<Calendar size={13}/>,     badge:occDates.length },
    { id:"comments",   label:"Comments",     icon:<MessageSquare size={13}/>,badge:notes.length    },
    { id:"activity",   label:"Activity Log", icon:<Activity size={13}/>,     badge:actLog.length   },
  ];

  const SUBTASK_TABS = [
    { id:"desc",     label:"Description",  icon:<AlignLeft size={13}/>,    badge:null         },
    { id:"attach",   label:"Attachments",  icon:<Paperclip size={13}/>,    badge:files.length  },
    { id:"comments", label:"Comments",     icon:<MessageSquare size={13}/>,badge:notes.length  },
    { id:"activity", label:"Activity Log", icon:<Activity size={13}/>,     badge:actLog.length },
  ];

  const TABS = isSubtask ? SUBTASK_TABS : MAIN_TABS;

  return (
    <>
      {open && (
        <div
          onClick={onClose}
          className={`fixed inset-0 bg-[rgba(15,23,42,.35)] backdrop-blur-[2px] ${isSubtaskView ? "z-[1599]" : "z-[1400]"}`}
        />
      )}

      <Lightbox src={lightboxSrc} onClose={()=>setLightboxSrc(null)}/>

      <div
        onClick={e=>e.stopPropagation()}
        className={`fixed top-0 right-0 bottom-0 bg-[#F8FAFC] flex flex-col overflow-y-auto transition-transform duration-[350ms] ease-[cubic-bezier(.4,0,.2,1)] ${
          isSubtaskView ? "w-[min(820px,94vw)] z-[1600]" : "w-[min(1100px,97vw)] z-[1401]"
        } ${open ? "translate-x-0" : "translate-x-full"}`}
      >

        {(updatingStatus || closingTask || reopeningTask) && (
          <div className="absolute inset-0 z-[99] flex flex-col items-center justify-center gap-3 bg-[rgba(248,250,252,0.78)] backdrop-blur-[2px]">
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-2xl shadow-[0_4px_24px_rgba(124,132,207,.25)] border border-[#EEEDFE]">
              <Loader size={18} color="rgb(124,132,207)" className="animate-spin"/>
              <span className="text-[13px] font-semibold text-[#3C3489]">Updating status…</span>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center flex-1 flex-col gap-3 min-h-screen">
            <div className="animate-spin flex">
              <Loader size={28} color="rgb(124,132,207)"/>
            </div>
            <span className="text-[13px] text-[#94A3B8]">Loading task details...</span>
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center justify-center flex-1 flex-col gap-3 min-h-screen">
            <span className="text-[14px] text-[#EF4444]">{error}</span>
            <button onClick={onClose} className="text-[13px] text-[rgb(124,132,207)] bg-transparent border-none cursor-pointer">Close</button>
          </div>
        )}

        {!loading && !error && task && (
          <div className="flex flex-col min-h-full">

            {/* ══ HEADER ══ */}
            <div className="bg-gradient-to-r from-green-700 to-green-500 shrink-0 px-5 py-[14px]">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <div className="text-[12px] text-[rgba(255,255,255,.75)] mb-[6px] flex items-center gap-[6px]">
                    <button onClick={onClose}
                      className="flex items-center gap-1 bg-transparent border-none cursor-pointer text-[rgba(255,255,255,.75)] text-[12px] font-[inherit]">
                      <ChevronLeft size={13}/>
                      {isSubtask ? "Subtasks" : "Tasks"}
                    </button>
                    <ChevronRight size={11}/>
                    <span className="text-white font-semibold">#{task.unique_seq_id}</span>
                    {isSubtask && (
                      <span className="bg-[rgba(255,255,255,.2)] text-white text-[10px] px-2 py-px rounded-full font-medium">
                        Subtask
                      </span>
                    )}
                  </div>
                  <div className="text-[18px] font-semibold text-white leading-[1.3]">{task.task_type}</div>
                  {isSubtask && task.parent_id_name && (
                    <div className="text-[11px] text-[rgba(255,255,255,.7)] mt-1">
                      Subtask of: <span className="text-white font-medium">{task.parent_id_name}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  {!isReadOnly && taskStatus==="open" && (
                    <button onClick={()=>changeStatus("in progress")}
                      className="flex items-center gap-[5px] px-4 py-[6px] bg-white text-[#3C3489] border-none rounded-[7px] text-[12px] font-semibold cursor-pointer">
                      <Play size={12}/> Start Task
                    </button>
                  )}
                  {!isReadOnly && taskStatus==="in progress" && (
                    <button onClick={()=>changeStatus("on hold")}
                      className="flex items-center gap-[5px] px-4 py-[6px] bg-[rgba(255,255,255,.9)] text-[#9D174D] border-none rounded-[7px] text-[12px] font-semibold cursor-pointer">
                      <Pause size={12}/> Pause Task
                    </button>
                  )}
                  {!isReadOnly && ["on hold","on-hold"].includes(taskStatus) && (
                    <button onClick={()=>changeStatus("in progress")}
                      className="flex items-center gap-[5px] px-4 py-[6px] bg-white text-[#3C3489] border-none rounded-[7px] text-[12px] font-semibold cursor-pointer">
                      <Play size={12}/> Resume Task
                    </button>
                  )}
                  {!isReadOnly && ["in progress","in-progress","on hold","on-hold"].includes(taskStatus) && (
                    <button
                      onClick={() => changeStatus(task.completion_approval_required === 1 ? "approval pending" : "completed")}
                      className="flex items-center gap-[5px] px-4 py-[6px] bg-[rgba(255,255,255,.9)] text-[#065F46] border-none rounded-[7px] text-[12px] font-semibold cursor-pointer">
                      <Check size={12}/> Complete Task
                    </button>
                  )}
                  {!isReadOnly && showApproveReject && (
                    <>
                      <button onClick={()=>changeStatus("completed")}
                        className="px-[14px] py-[6px] bg-[rgba(255,255,255,.9)] text-[#065F46] border-none rounded-[7px] text-[12px] font-semibold cursor-pointer">
                        Approve
                      </button>
                      <button onClick={()=>changeStatus("in progress")}
                        className="px-[14px] py-[6px] bg-[rgba(239,68,68,.85)] text-white border-none rounded-[7px] text-[12px] font-semibold cursor-pointer">
                        Reject
                      </button>
                      <button onClick={openCloseTaskModal}
                        className="px-[14px] py-[6px] bg-[rgba(0,0,0,.22)] text-white border-none rounded-[7px] text-[12px] font-semibold cursor-pointer">
                        ✕ Close Task
                      </button>
                    </>
                  )}
                  {!isReadOnly && isOpen && (
                    <button onClick={openCloseTaskModal}
                      className="flex items-center gap-[5px] px-[14px] py-[6px] bg-red-200 text-red-900 border-none rounded-[7px] text-[12px] font-semibold cursor-pointer">
                      ✕ Close Task
                    </button>
                  )}
                  {!isReadOnly && canReopen && taskStatus === "completed" && (
                    <button onClick={openReopenModal}
                      className="flex items-center gap-[5px] px-[14px] py-[6px] bg-[rgba(255,255,255,.9)] text-[#3C3489] border-none rounded-[7px] text-[12px] font-semibold cursor-pointer">
                      <RotateCcw size={12}/> Reopen Task
                    </button>
                  )}
                  <button onClick={onClose}
                    className="w-7 h-7 rounded-lg bg-[rgba(255,255,255,.15)] border border-[rgba(255,255,255,.3)] flex items-center justify-center cursor-pointer ml-1">
                    <X size={14} color="#fff"/>
                  </button>
                </div>
              </div>
            </div>

            {/* ══ DEADLINE APPROVAL BANNER ══ */}
            {showDeadlineApproval && (
              <div className="mx-[18px] mt-3 px-4 py-3 bg-[#FFFBEB] border-[1.5px] border-[#FCD34D] rounded-[10px] flex items-center justify-between gap-3 shrink-0">
                <div>
                  <div className="text-[12px] font-bold text-[#92400E] mb-1 flex items-center gap-[6px]">
                    <Clock size={13} color="#D97706"/> Deadline Extension Request
                  </div>
                  <div className="text-[11px] text-[#78350F]">
                    Current: <strong>{fmtLocalNaive(task.completion_sla)}</strong>
                    <span className="mx-[6px] text-[#D97706]">→</span>
                    Requested: <strong className="text-[#1d4ed8]">{fmtLocalNaive(task.changed_deadline)}</strong>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => handleDeadlineApproval("accept")}
                    className="flex items-center gap-[5px] px-[14px] py-[6px] rounded-[7px] border-none bg-[#059669] text-white text-[12px] font-semibold cursor-pointer font-[inherit]">
                    <Check size={12}/> Accept
                  </button>
                  <button
                    onClick={() => handleDeadlineApproval("reject")}
                    className="flex items-center gap-[5px] px-[14px] py-[6px] rounded-[7px] border-none bg-[#DC2626] text-white text-[12px] font-semibold cursor-pointer font-[inherit]">
                    <X size={12}/> Reject
                  </button>
                </div>
              </div>
            )}

            {/* ══ OVERDUE OCCURRENCE BANNER ══ */}
            {!!task.repeat && overdueOccs.length > 0 && (
              <div className="mx-[18px] mt-3 px-4 py-3 bg-[#FEF2F2] border-[1.5px] border-[#FCA5A5] rounded-[10px] flex items-center justify-between gap-3 shrink-0">
                <div className="flex items-start gap-[10px]">
                  <AlertTriangle size={15} color="#DC2626" className="shrink-0 mt-[1px]"/>
                  <div>
                    <div className="text-[12px] font-bold text-[#991B1B] mb-[2px]">
                      Occurrence {overdueOccs.length === 1 ? `#${overdueOccs[0]}` : `#${overdueOccs.join(", #")}`} Overdue
                    </div>
                    <div className="text-[11px] text-[#7F1D1D]">
                      {overdueOccs.length === 1
                        ? "This occurrence has passed its deadline without being completed."
                        : `${overdueOccs.length} occurrences have passed their deadlines without being completed.`}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setActiveTab("occurrence")}
                  className="shrink-0 px-3 py-[5px] rounded-[6px] border-none bg-[#DC2626] text-white text-[11px] font-semibold cursor-pointer font-[inherit]">
                  View
                </button>
              </div>
            )}

            {/* ══ ROW 1 + ROW 2 + SIDEBAR ══ */}
            <div
              className={`grid border-b-[0.5px] border-[#E8EAF0] bg-white [grid-template-columns:1fr_200px] ${
                (showDeadlineApproval || (task.repeat && overdueOccs.length > 0)) ? "mt-3" : "mt-0"
              }`}
            >
              <div className="p-[14px_18px] border-r-[0.5px] border-[#E8EAF0]">
                {/* Row 1 — RACI */}
                <div className="grid gap-[14px] pb-3 border-b-[0.5px] border-[#F1F5F9] mb-3 [grid-template-columns:repeat(4,1fr)]">
                  <div>
                    <LabelDark txt="Accountable"/>
                    <div className="text-[12px] text-[#1E293B] font-medium">{nameList(task.task_created_by_name||task.task_created_by)}</div>
                  </div>
                  <div>
                    <LabelDark txt="Responsible"/>
                    <div className="text-[12px] text-[#1E293B] font-medium">{nameList(task.assigned_to_name)}</div>
                  </div>
                  <div>
                    <LabelDark txt="Consulted"/>
                    {Array.isArray(task.observer_to_name)&&task.observer_to_name.length>0 ? (
                      <div className="flex flex-col gap-[3px]">
                        {task.observer_to_name.map((n,i)=>(
                          <span key={i} className="bg-[#EEEDFE] text-[#3C3489] text-[10px] px-2 py-[2px] rounded-full border-[0.5px] border-[#AFA9EC] w-fit">{n}</span>
                        ))}
                      </div>
                    ):<div className="text-[12px] text-[#1E293B]">{nameList(task.observer_to_name)}</div>}
                  </div>
                  <div>
                    <LabelDark txt="Informed"/>
                    {Array.isArray(task.informer_to_name)&&task.informer_to_name.length>0 ? (
                      <div className="flex flex-col gap-[3px]">
                        {task.informer_to_name.map((n,i)=>(
                          <span key={i} className="bg-[#EEEDFE] text-[#3C3489] text-[10px] px-2 py-[2px] rounded-full border-[0.5px] border-[#AFA9EC] w-fit">{n}</span>
                        ))}
                      </div>
                    ):<div className="text-[12px] text-[#1E293B]">{nameList(task.informer_to_name)}</div>}
                  </div>
                </div>

                {/* Row 2 — Meta */}
                <div className="flex flex-wrap gap-[14px] items-start">
                  <div>
                    <LabelDark txt="Status"/>
                    <span
                      className="inline-flex items-center gap-[5px] px-[10px] py-[3px] rounded-full text-[11px] font-semibold border"
                      style={{ background:sc.bg, color:sc.color, borderColor:(sc.dot||"#ccc")+"44" }}
                    >
                      <span className="w-[7px] h-[7px] rounded-full shrink-0" style={{ background:sc.dot }}/>{sc.label}
                    </span>
                  </div>
                  <div>
                    <LabelDark txt="Priority"/>
                    <span className="text-[11px] font-semibold px-[10px] py-[3px] rounded-full"
                      style={{ background:pc.bg, color:pc.color }}>
                      ⚑ {task.priority||"MEDIUM"}
                    </span>
                  </div>
                  <div>
                    <LabelDark txt="Workspace"/>
                    {(task.product_name||workspaceName)
                      ?<span className="bg-[#EAF3DE] text-[#27500A] text-[11px] font-semibold px-[10px] py-[3px] rounded-full border-[0.5px] border-[#97C459]">{task.product_name||workspaceName}</span>
                      :<span className="text-[12px] text-[#94A3B8]">—</span>}
                  </div>
                  <div>
                    <LabelDark txt="Start Date"/>
                    <div className={`text-[12px] ${task.start_date ? "text-[#1E293B]" : "text-[#94A3B8]"}`}>
                      {task.start_date?fmtLocalNaive(task.start_date):"Not specified"}
                    </div>
                  </div>
                  <div>
                    <LabelDark txt="Deadline"/>
                    <div className={`text-[12px] font-medium flex items-center gap-1 ${isOverdue ? "text-[#DC2626]" : "text-[#185FA5]"}`}>
                      {isOverdue&&<AlertTriangle size={11} color="#DC2626"/>}
                      {fmtLocalNaive(effectiveDeadline)}
                    </div>
                  </div>
                  {Array.isArray(task.functional_categories)&&task.functional_categories.length>0&&(
                    <div>
                      <LabelDark txt="Functional Categories"/>
                      <div className="flex flex-wrap gap-1">
                        {task.functional_categories.map((c,i)=>(
                          <span key={i} className="bg-[#EEEDFE] text-[#3C3489] text-[10px] px-2 py-[2px] rounded-full border-[0.5px] border-[#AFA9EC]">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {task.current_stage&&(
                    <div>
                      <LabelDark txt="Stage"/>
                      <div className="text-[12px] text-[#1E293B]">{task.current_stage}</div>
                    </div>
                  )}
                </div>
              </div>

              {!isReadOnly && (
              <div className="p-[14px_12px] bg-[#F8FAFC] flex flex-col gap-[6px]">
                <div className="text-[10px] font-semibold text-[#94A3B8] uppercase tracking-[.08em] mb-1">
                  {isSubtask ? "Actions" : "More Actions"}
                </div>

                {isSubtask ? (
                  canEditTask && <SideBtn icon={<Edit3 size={13}/>} label="Edit Subtask" onClick={()=>setShowEditTask(true)}/>
                ) : (
                  !["completed","closed"].includes(taskStatus) && (
                    <>
                      <SideBtn icon={<Forward size={13}/>}  label="Forward To"        onClick={()=>setShowForward(true)}/>
                      <SideBtn icon={<Clock size={13}/>}    label="Extended Deadline" onClick={()=>setShowExtDeadline(true)}/>
                      <SideBtn icon={<Plus size={13}/>}     label="Create Subtask"    onClick={()=>setShowCreateSubtask(true)}/>
                      {canEditTask && <SideBtn icon={<Edit3 size={13}/>} label="Edit Task" onClick={()=>setShowEditTask(true)}/> }
                    </>
                  )
                )}
              </div>
              )}
            </div>

            {/* ══ TABS ══ */}
            <div className="flex border-b-[0.5px] border-[#E8EAF0] bg-white overflow-x-auto shrink-0 [scrollbar-width:none] [-ms-overflow-style:none]">
              {TABS.map(tab=>{
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={()=>setActiveTab(tab.id)}
                    className={`flex items-center gap-[6px] px-4 py-[10px] text-[12px] font-semibold cursor-pointer bg-transparent whitespace-nowrap font-[inherit] -mb-px border-b-2 ${
                      isActive ? "border-[rgb(124,132,207)] text-[rgb(124,132,207)]" : "border-transparent text-[#64748B]"
                    }`}
                  >
                    <span className={`shrink-0 ${isActive ? "text-[rgb(124,132,207)]" : "text-[#94A3B8]"}`}>{tab.icon}</span>
                    {tab.label}
                    {tab.badge!==null&&(
                      <span className={`text-[10px] px-[6px] py-px rounded-full ${
                        isActive ? "bg-[#EEEDFE] text-[#3C3489]" : "bg-[#F1F5F9] text-[#64748B]"
                      }`}>{tab.badge}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* ══ TAB CONTENT ══ */}
            <div className="px-5 py-4 bg-white flex-1">

              {/* ── Description ── */}
              {activeTab==="desc" && (
                <div>
                  {isEditingDesc ? (
                    <div>
                      <EditorWrapper
                        editorNode={
                          <RichTextEditor theme="light" value={editDescText} onChange={html => setEditDescText(html)}/>
                        }
                      />
                      <div className="flex gap-2 mt-3">
                        <button onClick={saveDescription} disabled={savingDesc}
                          className="flex items-center gap-[6px] px-[18px] py-[7px] bg-[rgb(124,132,207)] text-white border-none rounded-lg text-[13px] font-semibold cursor-pointer font-[inherit]">
                          {savingDesc?<Loader size={12}/>:<Check size={12}/>} Save
                        </button>
                        <button onClick={()=>setIsEditingDesc(false)}
                          className="px-4 py-[7px] bg-[#F1F5F9] text-[#64748B] border-none rounded-lg text-[13px] font-semibold cursor-pointer font-[inherit]">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-[10px]">
                        <span className="text-[12px] text-[#94A3B8] font-semibold uppercase tracking-[.06em]">Content</span>
                        {taskStatus!=="closed" && (
                          <button onClick={()=>{ setEditDescText(task.detailed_description||task.description||""); setIsEditingDesc(true); }}
                            className="flex items-center gap-[5px] px-[10px] py-1 rounded-[7px] text-[12px] font-semibold cursor-pointer bg-[#EEEDFE] text-[#3C3489] border-[0.5px] border-[#AFA9EC]">
                            <Edit3 size={11}/> Edit
                          </button>
                        )}
                      </div>
                      {(task.detailed_description||task.description) ? (
                        <>
                          <div id="desc-display"
                            className={`text-[13px] text-[#374151] leading-[1.7] overflow-hidden [&_img]:cursor-zoom-in ${descExpanded ? "max-h-none" : "max-h-[220px]"}`}
                            onClick={e => { if(e.target.tagName==="IMG") setLightboxSrc(e.target.src); }}
                            dangerouslySetInnerHTML={{ __html:task.detailed_description||task.description }}/>
                          <button onClick={()=>setDescExpanded(p=>!p)}
                            className="mt-2 text-[12px] text-[rgb(124,132,207)] bg-transparent border-none cursor-pointer font-semibold p-0">
                            {descExpanded?"Show Less ▲":"View More ▼"}
                          </button>
                        </>
                      ):(
                        <p className="text-[13px] text-[#94A3B8] italic m-0">No description provided. Click Edit to add one.</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── Attachments ── */}
              {activeTab==="attach" && (
                <div>
                  <div className="flex justify-end mb-[14px]">
                    <button onClick={()=>setShowUpload(true)}
                      className="flex items-center gap-[6px] px-4 py-[7px] rounded-lg text-[12px] font-semibold cursor-pointer bg-[rgb(124,132,207)] text-white border-none">
                      <Upload size={12}/> Add Files
                    </button>
                  </div>
                  {files.length===0 ? (
                    <div onClick={()=>setShowUpload(true)}
                      className="rounded-xl py-10 px-5 text-center cursor-pointer bg-[#FAFBFE] border-2 border-dashed border-[#D1D5DB] hover:border-[rgb(124,132,207)] transition-colors">
                      <Upload size={28} color="#94A3B8" className="block mx-auto mb-[10px]"/>
                      <p className="text-[13px] text-[#64748B] m-0 font-medium">No attachments yet</p>
                      <p className="text-[11px] text-[#94A3B8] mt-1 mb-0">Click to upload or drag & drop</p>
                    </div>
                  ):(
                    <div className="flex flex-wrap gap-3">
                      {files.map((f,fi)=>(
                        <div key={fi} className="flex flex-col items-center gap-[6px] p-3 bg-[#FAFBFE] border-[0.5px] border-[#E8EAF0] rounded-[10px] w-[100px] transition-[box-shadow,border-color] duration-150 hover:shadow-[0_2px_8px_rgba(124,132,207,.2)] hover:border-[rgb(124,132,207)]">
                          <a href={f.download_url} target="_blank" rel="noreferrer"
                            className="flex flex-col items-center gap-[6px] no-underline cursor-pointer w-full">
                            <div className="w-[44px] h-[52px] bg-[#FEF2F2] border border-[#FECACA] rounded-lg flex items-center justify-center">
                              <FileText size={18} color="#EF4444"/>
                            </div>
                            <span className="text-[10px] text-[#64748B] text-center break-all leading-[1.3] max-w-full">{f.file_name}</span>
                          </a>
                          <div className="flex gap-1">
                            <a href={f.download_url} target="_blank" rel="noreferrer"
                              title="Download"
                              className="flex items-center justify-center w-6 h-6 rounded-[6px] bg-[#EEEDFE] text-[rgb(124,132,207)] no-underline">
                              <Download size={11}/>
                            </a>
                            {f.can_delete&&(
                              <button onClick={e=>{ e.stopPropagation(); setFileToDelete(f); }}
                                title="Delete"
                                className="flex items-center justify-center w-6 h-6 rounded-[6px] bg-[#FEE2E2] text-[#EF4444] border-none cursor-pointer">
                                <Trash2 size={11}/>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      <div onClick={()=>setShowUpload(true)}
                        className="flex flex-col items-center justify-center gap-[6px] p-3 rounded-[10px] w-[100px] cursor-pointer text-[#94A3B8] text-[11px] border-2 border-dashed border-[#D1D5DB] hover:border-[rgb(124,132,207)] hover:text-[rgb(124,132,207)] transition-colors">
                        <Plus size={20}/><span>Add More</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── RACI Matrix — main task only ── */}
              {activeTab==="raci" && !isSubtask && (
                <div className="overflow-x-auto rounded-lg border-[0.5px] border-[#E8EAF0]">
                  <table className="w-full border-collapse text-[12px]">
                    <thead>
                      <tr className="bg-[linear-gradient(135deg,rgb(124,132,207),rgb(187,196,255))]">
                        {["Task Action","R — Responsible","A — Accountable","C — Consulted","I — Informed"].map(h=>(
                          <th
                            key={h}
                            className={`px-3 py-2 text-[11px] font-semibold text-white whitespace-nowrap ${h === "Task Action" ? "text-left" : "text-center"}`}
                          >{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t-[0.5px] border-[#F1F5F9]">
                        <td className="p-[10px_12px]">
                          {Array.isArray(task.functional_categories)&&task.functional_categories.length>0 ? (
                            <div className="flex flex-wrap gap-1">
                              {task.functional_categories.map((cat,ci)=>(
                                <span key={ci} className="bg-[#EEEDFE] text-[#3C3489] text-[11px] px-2 py-[2px] rounded-full border-[0.5px] border-[#AFA9EC]">{cat}</span>
                              ))}
                            </div>
                          ) : <span className="text-[#94A3B8]">—</span>}
                        </td>
                        <td className="p-[10px_12px] text-center text-[#374151] text-[12px]">{nameList(task.assigned_to_name)}</td>
                        <td className="p-[10px_12px] text-center text-[#374151] text-[12px]">{nameList(task.task_created_by_name||task.task_created_by)}</td>
                        <td className="p-[10px_12px] text-center text-[#374151] text-[12px]">{nameList(task.observer_to_name)}</td>
                        <td className="p-[10px_12px] text-center text-[#374151] text-[12px]">{nameList(task.informer_to_name)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Subtasks — main task only, with click to open subtask detail ── */}
              {activeTab==="subtasks" && !isSubtask && (
                <div className="overflow-x-auto rounded-lg border-[0.5px] border-[#E8EAF0]">
                  <table className="w-full border-collapse text-[12px]">
                    <thead>
                      <tr className="bg-[#F8FAFC]">
                        {["NAME","DEADLINE","ACCOUNTABLE","RESPONSIBLE","STATUS"].map(h=>(
                          <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold text-[#94A3B8] tracking-[.06em]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {subtasks.length===0 ? (
                        <tr><td colSpan={5} className="px-3 py-6 text-center text-[13px] text-[#94A3B8]">No subtasks found</td></tr>
                      ):subtasks.map((s,si)=>{
                        const ssc=STATUS_CFG[(s.task_status||"").toLowerCase()]||STATUS_CFG["open"];
                        return (
                          <tr key={si} className="border-t-[0.5px] border-[#F1F5F9] hover:bg-[#F8FAFC]">
                            <td className="p-[10px_12px]">
                              <span onClick={() => setSubDetailId(s._id || s)}
                                className="text-[12px] font-medium text-[#1d4ed8] cursor-pointer">
                                {s.task_type}
                              </span>
                            </td>
                            <td className="p-[10px_12px] text-[#374151] text-[12px]">{fmtLocalNaive(s.completion_sla)}</td>
                            <td className="p-[10px_12px] text-[#374151] text-[12px]">{s.task_created_by_name||"—"}</td>
                            <td className="p-[10px_12px] text-[#374151] text-[12px]">{nameList(s.assigned_to_name)}</td>
                            <td className="p-[10px_12px]">
                              <span className="text-[10px] font-semibold px-2 py-[2px] rounded-full"
                                style={{ background:ssc.bg, color:ssc.color }}>{ssc.label}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Occurrences — main task only ── */}
              {activeTab==="occurrence" && !isSubtask && (
                occDates.length===0 ? (
                  <p className="text-[13px] text-[#94A3B8] text-center py-6">No occurrence dates found</p>
                ):(
                  <div className="overflow-x-auto rounded-lg border-[0.5px] border-[#E8EAF0]">
                    <table className="w-full border-collapse text-[12px]">
                      <thead>
                        <tr className="bg-[#F8FAFC]">
                          {["#","Start Date","Deadline","Status"].map(h=>(
                            <th key={h} className="px-3 py-[7px] text-left text-[11px] font-semibold text-[#94A3B8] tracking-[.06em]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {occDates.map((occ,di)=>{
                          const isObj    = typeof occ==="object"&&occ!==null;
                          const startDate= isObj?(occ.start_date||occ.start_date_utc||null):occ;
                          const deadline = isObj?(occ.completion_sla||occ.completion_sla_utc||null):null;
                          const status   = isObj?(occ.status||"—"):"—";
                          const isOccOverdue = deadline && new Date(deadline) < new Date()
                            && !["complete","completed","cancelled"].includes((status||"").toLowerCase());
                          const effectiveStatus = isOccOverdue ? "overdue" : status;
                          const ssc      = STATUS_CFG[(effectiveStatus||"").toLowerCase()]||{ bg:"#F1F5F9", color:"#475569" };
                          const rowBg = di % 2 === 0 ? "#fff" : "#FAFBFE";
                          return (
                            <tr key={di} className="border-t-[0.5px] border-[#F1F5F9]"
                              style={{ background: rowBg }}>
                              <td className="px-3 py-2 text-[#94A3B8] text-[11px]">{di+1}</td>
                              <td className="px-3 py-2 text-[#374151] text-[12px]">{fmtLocalNaive(startDate)}</td>
                              <td className="px-3 py-2 text-[#374151] text-[12px]">{fmtLocalNaive(deadline)}</td>
                              <td className="px-3 py-2">
                                <span className="text-[10px] font-semibold px-2 py-[2px] rounded-full"
                                  style={{ background:ssc.bg, color:ssc.color }}>{ssc.label || effectiveStatus}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* ── Comments ── */}
              {activeTab==="comments" && (
                <div>
                  {notes.length===0 ? (
                    <div className="text-center py-6 text-[#94A3B8] text-[13px] border-[0.5px] border-[#E8EAF0] rounded-lg mb-[14px]">
                      <MessageSquare size={22} className="block mx-auto mb-2 opacity-40"/>
                      No comments yet<br/>
                      <span className="text-[12px]">Be the first to comment!</span>
                    </div>
                  ):(
                    <div className="flex flex-col gap-3 mb-4">
                      {notes.map((n,ni)=>(
                        <div key={ni} className="flex gap-[10px]">
                          <Av name={n.user_name||"?"} size={30}/>
                          <div className="flex-1 bg-[#F8FAFC] rounded-[10px] px-[14px] py-[10px] border-[0.5px] border-[#E8EAF0]">
                            <div className="flex items-center justify-between mb-[6px]">
                              <span className="text-[12px] font-bold text-[#1E293B]">{n.user_name||"Unknown"}</span>
                              <span className="text-[11px] text-[#94A3B8]">{fmtDate(n.timestamp||n.date_time)}</span>
                            </div>
                            <div className="ql-editor !p-0 !text-[13px] !text-[#374151] !leading-[1.6] break-words"
                              dangerouslySetInnerHTML={{ __html: (n.comment||n.note||"")?.includes("<") ? (n.comment||n.note||"") : (n.comment||n.note||"")?.replace(/\n/g,"<br/>") }}/>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div ref={cmtWrapRef} className="rounded-[10px] overflow-hidden border-[1.5px] border-[#E2E8F0] [&:focus-within]:border-[#7C3AED] transition-colors duration-150">
                    <Editor
                      ref={cmtEditorRef}
                      value={commentText}
                      onTextChange={handleCommentChange}
                      placeholder="Add a comment... (@ to mention · Ctrl+Enter to post)"
                      style={{ height: "160px", fontSize: "13px" }}
                    />
                  </div>
                  <button onClick={postComment} disabled={isCommentEmpty(commentText)||postingCmt}
                    className={`mt-2 flex items-center gap-[6px] px-[18px] py-[7px] border-none rounded-lg text-[13px] font-semibold cursor-pointer font-[inherit] ${
                      !isCommentEmpty(commentText) ? "bg-[rgb(124,132,207)] text-white" : "bg-[#E8EAF0] text-[#94A3B8]"
                    }`}>
                    {postingCmt?<Loader size={12}/>:<Send size={12}/>} Add Comment
                  </button>
                </div>
              )}

              {/* ── Activity Log ── */}
              {activeTab==="activity" && (
                <div className="flex flex-col gap-[10px]">
                  {actLog.length===0 ? (
                    <div className="text-center py-6 text-[#94A3B8] text-[13px]">No activity yet</div>
                  ):[...actLog].reverse().map((log,li)=>(
                    <div key={li} className="flex gap-[10px] items-start">
                      <div className="w-7 h-7 rounded-full bg-[#EEEDFE] flex items-center justify-center shrink-0">
                        <Activity size={12} color="rgb(124,132,207)"/>
                      </div>
                      <div className="flex-1">
                        {log.log_type&&(
                          <span className="bg-[#F1F5F9] text-[#475569] text-[10px] font-semibold px-[7px] py-px rounded-full mb-1 inline-block">
                            {log.log_type}
                          </span>
                        )}
                        <p className="text-[13px] text-[#374151] my-[2px] leading-[1.5]">
                          {log.details||log.log_message||log.action||""}
                        </p>
                        <p className="text-[11px] text-[#94A3B8] m-0">
                          {log.log_user_name||""}
                          {log.assign_to&&log.assign_to!=="--"?` → ${log.assign_to}`:""}
                          {log.date_time?` · ${fmtUtcStr(log.date_time)}`:(log.log_time?` · ${fmtDate(log.log_time)}`:"")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        )}
      </div>
      {showUpload && (
        <AttachUploadModal onClose={()=>setShowUpload(false)} onUpload={handleUpload}/>
      )}
      {showForward && (
        <ForwardModal taskId={taskId} onClose={()=>setShowForward(false)}
          onSuccess={refreshTask} showToast={showToast}/>
      )}
      {showExtDeadline && (
        <ExtDeadlineModal taskId={taskId} onClose={()=>setShowExtDeadline(false)}
          onSuccess={refreshTask} showToast={showToast}/>
      )}
      {showCreateSubtask && task && (
        <CreateSubtaskModal task={task} onClose={()=>setShowCreateSubtask(false)}
          onSuccess={() => { refreshTask(); onAction(); setTimeout(refreshTask, 1500); }} showToast={showToast}/>
      )}
      {showEditTask && task && (
        <EditTaskModal task={task} onClose={()=>setShowEditTask(false)}
          onSuccess={() => { refreshTask(); onAction(); setTimeout(refreshTask, 1500); }} showToast={showToast}/>
      )}

      {subDetailId && (
        <TaskDetailPanel
          taskId={subDetailId}
          onClose={() => { setSubDetailId(null); refreshTask(); onAction(); }}
          isSubtaskView={true}
          onAction={() => { refreshTask(); onAction(); }}
        />
      )}

      {/* Reopen Task Modal */}
      {showReopenModal && (
        <div className="fixed inset-0 z-[1700] bg-black/[.45] flex items-center justify-center"
          onClick={() => setShowReopenModal(false)}>
          <div onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl w-[440px] max-w-[92vw] shadow-[0_20px_60px_rgba(0,0,0,.2)]">
            <div className="flex items-center justify-between px-[18px] py-[14px] border-b-[0.5px] border-[#E8EAF0]">
              <div className="flex items-center gap-2">
                <RotateCcw size={15} color="rgb(124,132,207)"/>
                <span className="text-[14px] font-bold text-[#1E293B]">Reopen Task</span>
              </div>
              <button onClick={() => setShowReopenModal(false)} className="bg-transparent border-none cursor-pointer">
                <X size={16} color="#64748B"/>
              </button>
            </div>
            <div className="p-[18px] flex flex-col gap-[14px]">
              {/* Reason */}
              <div>
                <label className="text-[12px] font-semibold text-[#64748B] block mb-[6px]">
                  Reason for Reopening <span className="text-[#EF4444]">*</span>
                </label>
                <textarea
                  value={reopenReason}
                  onChange={e => { setReopenReason(e.target.value); if (e.target.value.trim()) setReopenReasonErr(false); }}
                  placeholder="Why is this task being reopened?"
                  rows={3}
                  autoFocus
                  className={`w-full px-3 py-2 rounded-lg text-[13px] outline-none box-border font-[inherit] resize-none border ${
                    reopenReasonErr ? "border-[#EF4444]" : "border-[#E8EAF0]"
                  }`}
                />
                {reopenReasonErr && <p className="text-[12px] text-[#EF4444] mt-1 mb-0">Reason is required!</p>}
              </div>
              {/* Priority */}
              <div>
                <label className="text-[12px] font-semibold text-[#64748B] block mb-[6px]">
                  New Priority <span className="text-[#EF4444]">*</span>
                </label>
                <PriorityPickerDropdown value={reopenPriority} onChange={setReopenPriority} options={reopenPriorityOpts}/>
              </div>
              {/* Deadline */}
              <div>
                <label className="text-[12px] font-semibold text-[#64748B] block mb-[6px]">
                  New Deadline <span className="text-[#EF4444]">*</span>
                </label>
                <div className="flex gap-[6px]">
                  <input type="date"
                    value={reopenDeadline ? reopenDeadline.split("T")[0] : ""}
                    onChange={e => {
                      const d = e.target.value;
                      const t = reopenDeadline.split("T")[1] || "00:00";
                      setReopenDeadline(d ? `${d}T${t}` : "");
                      if (d) setReopenDeadlineErr(false);
                    }}
                    className={`flex-[3] px-3 py-2 rounded-lg border-[0.5px] text-[13px] outline-none box-border font-[inherit] ${
                      reopenDeadlineErr ? "border-[#EF4444]" : "border-[#E8EAF0]"
                    }`}
                  />
                  <input type="time"
                    value={reopenDeadline ? (reopenDeadline.split("T")[1] || "") : ""}
                    onChange={e => {
                      const d = reopenDeadline.split("T")[0];
                      if (d) setReopenDeadline(`${d}T${e.target.value}`);
                    }}
                    className="flex-[2] px-3 py-2 rounded-lg border-[0.5px] border-[#E8EAF0] text-[13px] outline-none box-border font-[inherit]"
                  />
                </div>
                {reopenDeadlineErr && <p className="text-[12px] text-[#EF4444] mt-1 mb-0">Deadline is required!</p>}
              </div>
            </div>
            <div className="flex gap-[10px] px-[18px] py-3 border-t-[0.5px] border-[#E8EAF0] bg-[#F8FAFC]">
              <button onClick={() => setShowReopenModal(false)}
                className="flex-1 p-[9px] rounded-[10px] border-[0.5px] border-[#E8EAF0] bg-white text-[13px] font-semibold cursor-pointer text-[#64748B] font-[inherit]">
                Cancel
              </button>
              <button onClick={confirmReopen} disabled={reopeningTask}
                className="flex-1 p-[9px] rounded-[10px] border-none text-[13px] font-semibold cursor-pointer font-[inherit] flex items-center justify-center gap-1.5 bg-[rgb(124,132,207)] text-white">
                {reopeningTask ? <Loader size={13}/> : <><RotateCcw size={13}/> Reopen Task</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Task Reason Modal */}
      {showCloseTaskModal && (
        <div className="fixed inset-0 z-[1700] bg-black/[.45] flex items-center justify-center"
          onClick={() => setShowCloseTaskModal(false)}>
          <div onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl w-[420px] max-w-[92vw] shadow-[0_20px_60px_rgba(0,0,0,.2)] overflow-hidden">
            <div className="flex items-center justify-between px-[18px] py-[14px] border-b-[0.5px] border-[#E8EAF0]">
              <span className="text-[14px] font-bold text-[#1E293B]">Close Task</span>
              <button onClick={() => setShowCloseTaskModal(false)} className="bg-transparent border-none cursor-pointer">
                <X size={16} color="#64748B"/>
              </button>
            </div>
            <div className="p-[18px]">
              <p className="text-[13px] text-[#64748B] mt-0 mb-[10px]">
                This task is not complete. A reason is required to close this task.
              </p>
              <textarea
                value={closeTaskReason}
                onChange={e => { setCloseTaskReason(e.target.value); if (e.target.value.trim()) setCloseTaskReasonErr(false); }}
                placeholder="Reason for closing this task..."
                rows={4}
                className={`w-full px-3 py-2 rounded-lg border-[0.5px] text-[13px] outline-none box-border font-[inherit] resize-none ${
                  closeTaskReasonErr ? "border-[#EF4444]" : "border-[#E8EAF0]"
                }`}
                autoFocus
              />
              {closeTaskReasonErr && (
                <p className="text-[12px] text-[#EF4444] mt-1 mb-0">Reason is required!</p>
              )}
            </div>
            <div className="flex gap-[10px] px-[18px] py-3 border-t-[0.5px] border-[#E8EAF0] bg-[#F8FAFC]">
              <button onClick={() => setShowCloseTaskModal(false)}
                className="flex-1 p-[9px] rounded-[10px] border-[0.5px] border-[#E8EAF0] bg-white text-[13px] font-semibold cursor-pointer text-[#64748B] font-[inherit]">
                Cancel
              </button>
              <button onClick={confirmCloseTask} disabled={closingTask}
                className="flex-1 p-[9px] rounded-[10px] border-none text-[13px] font-semibold cursor-pointer font-[inherit] flex items-center justify-center gap-1.5 bg-[#1E293B] text-white">
                {closingTask ? <Loader size={13}/> : "Close Task"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete attachment confirmation */}
      {fileToDelete && (
        <div className="fixed inset-0 z-[1700] bg-black/[.45] flex items-center justify-center"
          onClick={() => !deletingFile && setFileToDelete(null)}>
          <div onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl w-[400px] max-w-[92vw] shadow-[0_20px_60px_rgba(0,0,0,.2)] overflow-hidden">
            <div className="flex items-center justify-between px-[18px] py-[14px] border-b-[0.5px] border-[#E8EAF0]">
              <span className="text-[14px] font-bold text-[#1E293B]">Delete File</span>
              <button onClick={() => setFileToDelete(null)} disabled={deletingFile}
                className="bg-transparent border-none cursor-pointer">
                <X size={16} color="#64748B"/>
              </button>
            </div>
            <div className="p-[18px] flex gap-3 items-start">
              <div className="w-9 h-9 rounded-full bg-[#FEE2E2] flex items-center justify-center shrink-0">
                <AlertTriangle size={16} color="#EF4444"/>
              </div>
              <p className="text-[13px] text-[#64748B] m-0">
                Delete <span className="font-semibold text-[#1E293B]">"{fileToDelete.file_name}"</span>? This cannot be undone.
              </p>
            </div>
            <div className="flex gap-[10px] px-[18px] py-3 border-t-[0.5px] border-[#E8EAF0] bg-[#F8FAFC]">
              <button onClick={() => setFileToDelete(null)} disabled={deletingFile}
                className="flex-1 p-[9px] rounded-[10px] border-[0.5px] border-[#E8EAF0] bg-white text-[13px] font-semibold cursor-pointer text-[#64748B] font-[inherit]">
                Cancel
              </button>
              <button onClick={confirmDeleteFile} disabled={deletingFile}
                className="flex-1 p-[9px] rounded-[10px] border-none text-[13px] font-semibold cursor-pointer font-[inherit] flex items-center justify-center gap-1.5 bg-[#EF4444] text-white">
                {deletingFile ? <Loader size={13}/> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* @mention dropdown */}
      {mentionQuery !== null && raciMembers.length > 0 && (
        <div className="fixed bg-white border border-[#E2E8F0] rounded-[12px] shadow-[0_8px_32px_rgba(109,40,217,.18)] overflow-y-auto max-h-[280px] z-[9999]"
          style={{
            top:       dropdownPos.top    != null ? dropdownPos.top    : "auto",
            bottom:    dropdownPos.bottom != null ? dropdownPos.bottom : "auto",
            left:      dropdownPos.left,
            width:     dropdownPos.width,
          }}>
          <div className="px-3 py-[7px] border-b border-[#F1F5F9] bg-[#FAFBFF] flex items-center gap-2">
            <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-[.08em]">Task Members</span>
            {mentionQuery && (
              <span className="text-[10px] text-[#7C3AED] font-semibold bg-[#F5F3FF] px-[6px] py-[1px] rounded-full">
                {mentionQuery}
              </span>
            )}
          </div>
          {filteredMentions.length === 0 ? (
            <div className="px-4 py-3 text-[13px] text-[#94A3B8] text-center">No members match "{mentionQuery}"</div>
          ) : (
            filteredMentions.map((member, i) => (
              <div key={member}
                className={`flex items-center gap-[10px] px-3 py-[9px] cursor-pointer transition-colors duration-100 ${i === mentionIndex ? "bg-[#F5F3FF]" : "hover:bg-[#F8FAFC]"}`}
                onMouseDown={e => { e.preventDefault(); insertMention(member); }}
                onMouseEnter={() => setMentionIndex(i)}>
                <Av name={member} size={28}/>
                <span className={`text-[13px] font-semibold ${i === mentionIndex ? "text-[#7C3AED]" : "text-[#374151]"}`}>
                  {member}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type}/>}
    </>
  );
}